import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { css, cx } from 'emotion';
import SearchIcon from '@frontend/static/icons/search.svg';
import { brand, brandText, brandAlt } from '@guardian/src-foundations/palette';
import { textSans } from '@guardian/src-foundations/typography';
import { from } from '@guardian/src-foundations/mq';
import { getCookie } from '@root/src/web/browser/cookie';
import { Dropdown } from '@root/src/web/components/Dropdown';
import ProfileIcon from '@frontend/static/icons/profile.svg';
import { createAuthenticationEventParams } from '@root/src/lib/identity-component-event';
import { useOnce } from '@frontend/web/lib/useOnce';
import { getZIndex } from '../lib/getZIndex';
const linkStyles = css `
	${textSans.medium()};
	color: ${brandText.primary};
	float: left;
	position: relative;
	transition: color 80ms ease-out;
	text-decoration: none;
	padding: 7px 0;

	${from.tablet} {
		padding: 5px 7px;
	}

	:hover,
	:focus {
		color: ${brandAlt[400]};
	}

	svg {
		fill: currentColor;
		float: left;
		height: 18px;
		width: 18px;
		margin: 3px 4px 0 0;
	}
`;
const searchLinkStyles = css `
	${linkStyles}
	${getZIndex('searchHeaderLink')}
`;
const linkTablet = ({ showAtTablet }) => css `
	display: none;

	${from.tablet} {
		display: ${showAtTablet ? 'block' : 'none'};
	}

	${from.desktop} {
		display: block;
	}
`;
const seperatorStyles = css `
	border-left: 1px solid ${brand[600]};
	float: left;
	height: 24px;
	margin: 0 -2px 0 10px;
	display: none;

	${from.desktop} {
		display: block;
	}
`;
const seperatorHideStyles = css `
	border-left: 1px solid ${brand[600]};
	float: left;
	height: 24px;
	margin: 0 -2px 0 10px;
	display: none;

	${from.tablet} {
		display: block;
	}
`;
const Search = ({ className, children, href, dataLinkName, }) => (_jsx("a", Object.assign({ href: href, className: className, "data-link-name": dataLinkName }, { children: children }), void 0));
const linksStyles = css `
	position: absolute;
	left: 10px;
	top: 0;

	${from.mobileLandscape} {
		left: 20px;
	}

	${from.tablet} {
		left: auto;
		right: 205px;
	}

	${from.desktop} {
		right: 266px;
	}

	${from.wide} {
		right: 342px;
	}
`;
export const Links = ({ userId, supporterCTA, idUrl: idUrlFromConfig, mmaUrl: mmaUrlFromConfig, }) => {
    const [showSupporterCTA, setShowSupporterCTA] = useState();
    const [userIsDefined, setUserIsDefined] = useState();
    // show supporter CTA if support messaging isn't shown
    useEffect(() => {
        setShowSupporterCTA(getCookie('gu_hide_support_messaging') === 'true');
    }, []);
    // we intentionally re-render here because we know the DOM structure could be different
    // from the server rendered version. This forces a full validation
    useOnce(() => {
        setUserIsDefined(!!userId);
    }, [userId]);
    // Fall back on prod URLs just in case these aren't set for any reason
    const idUrl = idUrlFromConfig || 'https://profile.theguardian.com';
    const mmaUrl = mmaUrlFromConfig || 'https://manage.theguardian.com';
    const identityLinks = [
        {
            url: `${mmaUrl}/`,
            title: 'Account overview',
            dataLinkName: 'nav2 : topbar : account overview',
        },
        {
            url: `${mmaUrl}/public-settings`,
            title: 'Profile',
            dataLinkName: 'nav2 : topbar : edit profile',
        },
        {
            url: `${mmaUrl}/email-prefs`,
            title: 'Emails & marketing',
            dataLinkName: 'nav2 : topbar : email prefs',
        },
        {
            url: `${mmaUrl}/account-settings`,
            title: 'Settings',
            dataLinkName: 'nav2 : topbar : settings',
        },
        {
            url: `${mmaUrl}/help`,
            title: 'Help',
            dataLinkName: 'nav2 : topbar : help',
        },
        {
            url: `${idUrl}/user/id/${userId}`,
            title: 'Comments & replies',
            dataLinkName: 'nav2 : topbar : comment activity',
        },
        {
            url: `${idUrl}/signout`,
            title: 'Sign out',
            dataLinkName: 'nav2 : topbar : sign out',
        },
    ];
    return (_jsxs("div", Object.assign({ "data-print-layout": "hide", className: linksStyles }, { children: [showSupporterCTA && supporterCTA !== '' && (_jsxs(_Fragment, { children: [_jsx("div", { className: seperatorStyles }, void 0),
                    _jsx("a", Object.assign({ href: supporterCTA, className: cx(linkTablet({ showAtTablet: false }), linkStyles), "data-link-name": "nav2 : supporter-cta" }, { children: "Subscriptions" }), void 0)] }, void 0)),
            _jsx("div", { className: seperatorStyles }, void 0),
            _jsx("a", Object.assign({ href: "https://jobs.theguardian.com/?INTCMP=jobs_uk_web_newheader", className: cx(linkTablet({ showAtTablet: false }), linkStyles), "data-link-name": "nav2 : job-cta" }, { children: "Search jobs" }), void 0),
            _jsx("div", { className: seperatorHideStyles }, void 0),
            userIsDefined ? (_jsxs("div", Object.assign({ className: linkStyles }, { children: [_jsx(ProfileIcon, {}, void 0),
                    _jsx(Dropdown, { label: "My account", links: identityLinks, id: "my-account", dataLinkName: "nav2 : topbar: my account" }, void 0)] }), void 0)) : (_jsxs("a", Object.assign({ className: linkStyles, href: `${idUrl}/signin?INTCMP=DOTCOM_NEWHEADER_SIGNIN&ABCMP=ab-sign-in&${createAuthenticationEventParams('guardian_signin_header')}`, "data-link-name": "nav2 : topbar : signin" }, { children: [_jsx(ProfileIcon, {}, void 0), " Sign in"] }), void 0)),
            _jsxs(Search, Object.assign({ className: cx(linkTablet({ showAtTablet: false }), searchLinkStyles), href: "https://www.google.co.uk/advanced_search?q=site:www.theguardian.com", dataLinkName: "nav2 : search" }, { children: [_jsx(SearchIcon, {}, void 0),
                    _jsx(_Fragment, { children: "Search" }, void 0)] }), void 0)] }), void 0));
};
